import { HTTPClient } from "@/common/HTTPClient";

// Matches the flat fastapi_pagination response: { items, total, page, size }.
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

export type RvsProcessStatus = "pending" | "processing" | "success" | "failed";

export interface RvsRawDataFileParams {
  sensorId?: string;
  rawDataDateFrom?: string;
  rawDataDateTo?: string;
  processStatus?: RvsProcessStatus;
  order?: "asc" | "desc";
  page?: number;
  size?: number;
}

export interface RvsRawDataFile {
  id: number;
  sensorId: string;
  fileName: string;
  rawDataDate: string | null;
  latestCalDate: string | null;
  processStatus: RvsProcessStatus;
  errorMessage: string | null;
  jobId: number | null;
}

export interface RvsCalculationJob {
  id: number;
  type: string;
  requestedBy: string | null;
  status: "running" | "completed" | "completed_with_errors" | "failed";
  totalFiles: number;
  doneFiles: number;
  failedFiles: number;
  startDate: string | null;
  endDate: string | null;
  // threshold_fm jobs only (null for spectrum jobs).
  sensorId: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  errorMessage: string | null;
  files: RvsRawDataFile[];
}

export interface ThresholdFmRow {
  parameterType: string;
  axis: string;
  wn: number;
  al: number;
}

export interface ThresholdFmResponse {
  exists: boolean;
  lastModified: string | null;
  rows: ThresholdFmRow[];
}

export interface ThresholdFmFilesResponse {
  count: number;
  firstFile: string | null;
  lastFile: string | null;
}

export class RvsCalculationClient {
  constructor(private httpClient: HTTPClient) {}

  public async getSensorIds(search?: string): Promise<string[]> {
    const { data } = await this.httpClient.request({
      url: "/api/v1/rvs-calculation/sensors",
      method: "GET",
      params: { search: search || undefined },
    });
    return data as string[];
  }

  public async getRawDataFiles(params: RvsRawDataFileParams): Promise<PaginatedResponse<RvsRawDataFile>> {
    const { data } = await this.httpClient.request({
      url: "/api/v1/rvs-calculation/raw-data-files",
      method: "GET",
      params,
    });
    return data as PaginatedResponse<RvsRawDataFile>;
  }

  public async createJob(fileIds: number[]): Promise<{ jobId: number }> {
    const { data } = await this.httpClient.request({
      url: "/api/v1/rvs-calculation/jobs",
      method: "POST",
      data: { fileIds },
    });
    return data as { jobId: number };
  }

  public async getJob(jobId: number): Promise<RvsCalculationJob> {
    const { data } = await this.httpClient.request({
      url: `/api/v1/rvs-calculation/jobs/${jobId}`,
      method: "GET",
    });
    return data as RvsCalculationJob;
  }

  public async getActiveJob(): Promise<RvsCalculationJob | null> {
    const { data } = await this.httpClient.request({
      url: "/api/v1/rvs-calculation/jobs/active",
      method: "GET",
    });
    return data as RvsCalculationJob | null;
  }

  public async getThresholdFm(sensorId: string): Promise<ThresholdFmResponse> {
    const { data } = await this.httpClient.request({
      url: "/api/v1/rvs-calculation/threshold-fm",
      method: "GET",
      params: { sensorId },
    });
    return data as ThresholdFmResponse;
  }

  public async updateThresholdFm(sensorId: string, rows: ThresholdFmRow[]): Promise<ThresholdFmResponse> {
    const { data } = await this.httpClient.request({
      url: "/api/v1/rvs-calculation/threshold-fm",
      method: "PUT",
      data: { sensorId, rows },
    });
    return data as ThresholdFmResponse;
  }

  public async getThresholdFmFiles(sensorId: string, dateFrom?: string, dateTo?: string): Promise<ThresholdFmFilesResponse> {
    const { data } = await this.httpClient.request({
      url: "/api/v1/rvs-calculation/threshold-fm/files",
      method: "GET",
      params: { sensorId, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined },
    });
    return data as ThresholdFmFilesResponse;
  }

  public async createThresholdFmJob(sensorId: string, dateFrom: string, dateTo: string): Promise<{ jobId: number }> {
    const { data } = await this.httpClient.request({
      url: "/api/v1/rvs-calculation/threshold-fm/jobs",
      method: "POST",
      data: { sensorId, dateFrom, dateTo },
    });
    return data as { jobId: number };
  }
}
